import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { createAuditLog } from '../utils/auditLogger';
import { MODULE_PERMISSION_ALIASES } from '../config/permissions';
import { DEFAULT_ROLES, ModuleName, PermissionAction } from '../types/roles';
import { Platform, getPlatform } from '../config/platform';
import { authService } from '../services/authService';
import { notificationService } from '../services/notificationService';
import { pushNotificationService } from '../services/pushNotificationService';
import { roleService } from '../services/roleService';
import { getUserRoles, isAnyDriverUser } from '../utils/userRoles';
import { ApiError } from '../services/apiService';

export type UserRole = 'admin' | 'internal-driver' | 'external-driver' | 'dispatcher' | 'warehouse' | 'accountant' | 'planner' | string;

export interface User {
  id: number | string;
  name: string;
  username?: string;
  email: string;
  role: UserRole;
  roles?: UserRole[];
  roleIds?: string[];
  roleId?: string;
  avatar?: string;
  phone?: string;
  department?: string;
  position?: string;
  startDate?: string;
  address?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relation: string;
  };
  platform?: Platform;
  lastLoginPlatform?: Platform;
  driverId?: number;
  driverType?: string;
  externalTransportAccountUserId?: number | string | null;
  isImpersonating?: boolean;
  impersonatedById?: number | string | null;
  impersonatedByName?: string | null;
  originalUserId?: number | string | null;
  isPaymentPreapprovalController?: boolean;
  isPaymentFinalApprover?: boolean;
  canPriceMismatchApprove?: boolean;
  canManualDemurrageEdit?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, role?: UserRole) => Promise<{ success: boolean; message?: string; requiresTwoFactor?: boolean; maskedPhone?: string; email?: string }>;
  completeTwoFactorLogin: (email: string, code: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  impersonateUser: (userId: string | number) => Promise<{ success: boolean; message?: string; user?: User | null }>;
  restoreOriginalUser: () => Promise<{ success: boolean; message?: string; user?: User | null }>;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  updateProfile: (updates: Partial<User>) => void;
  hasPermission: (module: ModuleName, action: PermissionAction) => boolean;
  hasExactPermission: (module: ModuleName, action: PermissionAction) => boolean;
  currentPlatform: Platform;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const resolveUserRoles = (source: any): UserRole[] => getUserRoles(source) as UserRole[];
const parseRoleIds = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return String(value).split(',').map((item) => String(item || '').trim()).filter(Boolean);
    }
  }
  return [];
};

const STATIC_ROLE_PERMISSIONS: Record<string, Partial<Record<ModuleName, PermissionAction[]>>> = {
  'corporate-external-transport': {
    dashboard: ['read'],
    'bid-system': ['read', 'create', 'update'],
    'bid-marketplace': ['read', 'create'],
    'my-bids': ['read', 'update'],
    'active-jobs': ['read'],
    'shipment-tracking': ['read'],
    'driver-tracking': ['read'],
    'corporate-external-vehicles': ['create', 'read', 'update'],
    'vehicle-management': ['create', 'read', 'update'],
    'trailer-management': ['create', 'read', 'update'],
    'approved-drivers': ['create', 'read', 'update'],
    'driver-applications': ['create', 'read', 'update'],
    'accounting-workflow': ['read', 'update'],
    documents: ['create', 'read', 'update'],
    'support-center': ['read', 'create'],
    profile: ['read', 'update'],
  },
  'corporate-external-transport-user': {
    dashboard: ['read'],
    'bid-system': ['read', 'create', 'update'],
    'bid-marketplace': ['read', 'create'],
    'my-bids': ['read', 'update'],
    'active-jobs': ['read'],
    'shipment-tracking': ['read'],
    'driver-tracking': ['read'],
    'corporate-external-vehicles': ['create', 'read', 'update'],
    'vehicle-management': ['create', 'read', 'update'],
    'trailer-management': ['create', 'read', 'update'],
    'approved-drivers': ['create', 'read', 'update'],
    'driver-applications': ['create', 'read', 'update'],
    'accounting-workflow': ['read', 'update'],
    documents: ['create', 'read', 'update'],
    'support-center': ['read', 'create'],
    profile: ['read', 'update'],
  },
  'corporate-external-transport-driver': {
    dashboard: ['read'],
    'active-jobs': ['read', 'update'],
    'shipment-tracking': ['read', 'update'],
    'driver-tracking': ['read', 'update'],
    profile: ['read', 'update'],
  },
};

const mapUserToContextUser = (source: any, currentPlatform: Platform, driver?: any): User => {
  const sourceRoles = resolveUserRoles(source);
  const isCorporateSubUser = sourceRoles.includes('corporate-external-transport-user') || (sourceRoles.includes('corporate-external-transport') && Boolean(source.externalTransportAccountUserId || source.external_transport_account_user_id));
  const effectiveRoles = isCorporateSubUser ? ['external-driver'] : sourceRoles;

  return {
    id: source.id,
    name: source.full_name || source.fullName || source.username || source.name || '',
    username: source.username,
    email: source.email || '',
    role: isCorporateSubUser ? 'external-driver' : (source.role || 'external-driver'),
    roles: effectiveRoles,
    roleIds: parseRoleIds(source.role_ids || source.roleIds),
    roleId: (source.role_id || source.roleId || '').toString() || undefined,
    phone: source.phone || '',
    department: source.department_id?.toString(),
    platform: currentPlatform,
    lastLoginPlatform: currentPlatform,
    driverId: driver?.id ?? source.driverId,
    driverType: isCorporateSubUser ? 'external' : (driver?.driver_type || driver?.type || source.driverType),
    externalTransportAccountUserId: driver?.external_transport_account_user_id ?? source.external_transport_account_user_id ?? source.externalTransportAccountUserId ?? null,
    isImpersonating: Boolean(source.isImpersonating),
    impersonatedById: source.impersonatedById ?? null,
    impersonatedByName: source.impersonatedByName ?? null,
    originalUserId: source.originalUserId ?? null,
    isPaymentPreapprovalController: Boolean(source.is_payment_preapproval_controller ?? source.isPaymentPreapprovalController),
    isPaymentFinalApprover: Boolean(source.is_payment_final_approver ?? source.isPaymentFinalApprover),
    canPriceMismatchApprove: Boolean(source.can_price_mismatch_approve ?? source.canPriceMismatchApprove),
    canManualDemurrageEdit: Boolean(source.can_manual_demurrage_edit ?? source.canManualDemurrageEdit),
  };
};

export { AuthContext };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleDefinitions, setRoleDefinitions] = useState<any[]>([]);
  const currentPlatform = getPlatform();
  const shouldLoadRoleDefinitions = useCallback((source: any) => !isAnyDriverUser(source), []);

  const refreshRoleDefinitions = useCallback(async () => {
    try {
      // Every user needs their own role permissions to build the menu.
      // The full role catalogue remains restricted to role-management users.
      const roles = await roleService.getAssignedRoles();
      setRoleDefinitions(roles);
      return roles;
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 403)) {
        console.error('Role definitions load error:', error);
      }
      try {
        const cached = JSON.parse(localStorage.getItem('roles') || '[]');
        setRoleDefinitions(Array.isArray(cached) ? cached : []);
      } catch {
        setRoleDefinitions([]);
      }
      return [];
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser();
          
          if (storedUser) {
            if (shouldLoadRoleDefinitions(storedUser)) {
              await refreshRoleDefinitions();
            } else {
              setRoleDefinitions([]);
            }
            const mappedUser: User = mapUserToContextUser(storedUser, currentPlatform, authService.getStoredDriver());
            setUser(mappedUser);
          } else {
            const response = await authService.getCurrentUser();
            if (response.success && response.data) {
              const apiUser = response.data;
              if (shouldLoadRoleDefinitions(apiUser)) {
                await refreshRoleDefinitions();
              } else {
                setRoleDefinitions([]);
              }
              const mappedUser: User = mapUserToContextUser(apiUser, currentPlatform, authService.getStoredDriver());
              setUser(mappedUser);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [currentPlatform, refreshRoleDefinitions, shouldLoadRoleDefinitions]);

  const login = useCallback(async (email: string, password: string, role?: UserRole): Promise<{ success: boolean; message?: string; requiresTwoFactor?: boolean; maskedPhone?: string; email?: string }> => {
    try {
      const response = await authService.login({ email, password });

      if (response.data?.requiresTwoFactor && !response.data?.token) {
        return {
          success: false,
          requiresTwoFactor: true,
          maskedPhone: response.data.maskedPhone,
          email: response.data.email || email,
        };
      }

      if (response.success && response.data) {
        const userData = response.data.user;
        if (shouldLoadRoleDefinitions(userData)) {
          await refreshRoleDefinitions();
        } else {
          setRoleDefinitions([]);
        }
        
        if (role && userData.role !== role) {
          console.warn('Role mismatch: expected', role, 'got', userData.role);
        }
        
         const mappedUser: User = mapUserToContextUser({ ...userData, role: userData.role || role }, currentPlatform, response.data.driver);
        
        setUser(mappedUser);

        const storedPushToken = localStorage.getItem('pushToken');
        if (storedPushToken) {
          notificationService.registerPushToken(storedPushToken, currentPlatform).catch((error) => {
            console.error('Stored push token registration failed:', error);
          });
        }
        pushNotificationService.registerCurrentToken().catch((error) => {
          console.error('Push token refresh after login failed:', error);
        });
        
        createAuditLog({
          category: 'AUTH',
          action: 'LOGIN',
          entity: 'User',
          entityId: String(mappedUser.id),
          entityName: mappedUser.name,
          description: `${mappedUser.name} sisteme giriş yaptı`,
          severity: 'info',
        });
        
        return { success: true };
      }
      
      return {
        success: false,
        message: response.message || 'Email veya şifre hatalı',
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Giriş sırasında bir hata oluştu',
      };
    }
  }, [currentPlatform, refreshRoleDefinitions, shouldLoadRoleDefinitions]);

  const completeTwoFactorLogin = useCallback(async (email: string, code: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authService.verifyLoginCode(email.trim(), code.trim());

      if (response.success && response.data) {
        const userData = response.data.user;
        if (shouldLoadRoleDefinitions(userData)) {
          await refreshRoleDefinitions();
        } else {
          setRoleDefinitions([]);
        }

        const mappedUser: User = mapUserToContextUser(userData, currentPlatform, response.data.driver);
        setUser(mappedUser);

        const storedPushToken = localStorage.getItem('pushToken');
        if (storedPushToken) {
          notificationService.registerPushToken(storedPushToken, currentPlatform).catch((error) => {
            console.error('Stored push token registration failed:', error);
          });
        }
        pushNotificationService.registerCurrentToken().catch((error) => {
          console.error('Push token refresh after login failed:', error);
        });

        createAuditLog({
          category: 'AUTH',
          action: 'LOGIN',
          entity: 'User',
          entityId: String(mappedUser.id),
          entityName: mappedUser.name,
          description: `${mappedUser.name} sisteme (SMS doğrulama) giriş yaptı`,
          severity: 'info',
        });

        return { success: true };
      }

      return {
        success: false,
        message: response.message || 'Doğrulama başarısız',
      };
    } catch (error) {
      console.error('Two-factor login error:', error);
      return {
        success: false,
        message: 'Doğrulama sırasında bir hata oluştu',
      };
    }
  }, [currentPlatform, refreshRoleDefinitions, shouldLoadRoleDefinitions]);

  const logout = useCallback(() => {
    if (user) {
      createAuditLog({
        category: 'AUTH',
        action: 'LOGOUT',
        entity: 'User',
        entityId: String(user.id),
        entityName: user.name,
        description: `${user.name} sistemden çıkış yaptı`,
        severity: 'info',
      });
    }
    
    authService.logout();
    setUser(null);
  }, [user]);

  const impersonateUser = useCallback(async (userId: string | number) => {
    try {
      const response = await authService.impersonate(userId);
      if (!response.success || !response.data?.user) {
        return { success: false, message: response.message || 'Bu kullanıcı ile giriş yapılamadı', user: null };
      }

      const userData = mapUserToContextUser(response.data.user, currentPlatform, response.data.driver);
      if (shouldLoadRoleDefinitions(userData)) {
        await refreshRoleDefinitions();
      } else {
        setRoleDefinitions([]);
      }
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('Impersonate user error:', error);
      return { success: false, message: 'Bu kullanıcı ile giriş yapılamadı', user: null };
    }
  }, [currentPlatform, refreshRoleDefinitions, shouldLoadRoleDefinitions]);

  const restoreOriginalUser = useCallback(async () => {
    const original = authService.restoreOriginalSession();
    if (!original?.user) {
      return { success: false, message: 'Ana hesap bilgisi bulunamadı', user: null };
    }

    const mappedUser = mapUserToContextUser(original.user, currentPlatform, original.driver);
    if (shouldLoadRoleDefinitions(mappedUser)) {
      await refreshRoleDefinitions();
    } else {
      setRoleDefinitions([]);
    }
    setUser(mappedUser);
    return { success: true, user: mappedUser };
  }, [currentPlatform, refreshRoleDefinitions, shouldLoadRoleDefinitions]);

  const updateProfile = useCallback((updates: Partial<User>) => {
    setUser(currentUser => {
      if (!currentUser) return null;
      const updatedUser = { ...currentUser, ...updates };
      
      createAuditLog({
        category: 'USER',
        action: 'UPDATE',
        entity: 'User',
        entityId: String(updatedUser.id),
        entityName: updatedUser.name,
        description: `${updatedUser.name} profilini güncelledi`,
        severity: 'info',
      });
      
      return updatedUser;
    });
  }, []);

  const hasExactPermission = useCallback((module: ModuleName, action: PermissionAction): boolean => {
    if (!user) return false;
    
    const effectiveRoles = resolveUserRoles(user);
    if (effectiveRoles.includes('admin') || effectiveRoles.includes('super-admin')) return true;

    const knownRoles = roleDefinitions.length > 0
      ? roleDefinitions
      : (() => {
          try {
            const cached = JSON.parse(localStorage.getItem('roles') || '[]');
            return Array.isArray(cached) && cached.length > 0 ? cached : DEFAULT_ROLES;
          } catch {
            return DEFAULT_ROLES;
          }
        })();

    if (effectiveRoles.some((role) => (STATIC_ROLE_PERMISSIONS[role]?.[module] || []).includes(action))) {
      return true;
    }

    return knownRoles
      .filter((role: any) => effectiveRoles.includes(String(role.id)))
      .some((role: any) =>
        Array.isArray(role.permissions) && role.permissions.some((permission: any) =>
          permission.module === module && permission.actions?.includes(action)
        )
      );
  }, [roleDefinitions, user]);

  const hasPermission = useCallback((module: ModuleName, action: PermissionAction): boolean => {
    if (hasExactPermission(module, action)) return true;
    return (MODULE_PERMISSION_ALIASES[module] || []).some((alias) => hasExactPermission(alias, action));
  }, [hasExactPermission]);

  const value = useMemo(
    () => ({
      user,
      login,
      completeTwoFactorLogin,
      logout,
      impersonateUser,
      restoreOriginalUser,
      isAuthenticated: !!user,
      isImpersonating: !!user?.isImpersonating,
      updateProfile,
      hasPermission,
      hasExactPermission,
      currentPlatform,
      loading,
    }),
    [user, login, completeTwoFactorLogin, logout, impersonateUser, restoreOriginalUser, updateProfile, hasPermission, hasExactPermission, currentPlatform, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('useAuth hook\'u AuthProvider dışında kullanılıyor.');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
